import { getStoryblokApi } from "@storyblok/react";
import axios from "axios";
import { ContactUSFormFields, ZendeskBody } from "./ContactUs/ContactUsModel";

class SupportUtils {
  static zendeskUrl = "https://audiomovers.zendesk.com/api/v2/";

  static async getCachedOrStoryblokSearch(
    query: string | null,
    itemsPerPage: number | undefined,
    popular: boolean | null = null,
    category: string | null = null,
    tag: string | null = null,
    starts_with: string | undefined = undefined
  ) {
    try {
      return await this.getSearch(
        query,
        itemsPerPage,
        popular,
        category,
        tag,
        starts_with
      );
    } catch {
      return await this.getStoryblokSearch(
        query,
        itemsPerPage,
        popular,
        category,
        tag,
        starts_with
      );
    }
  }

  static getSearch = (
    query: string | null,
    itemsPerPage: number | undefined,
    popular: boolean | null = null,
    category: string | null = null,
    tag: string | null = null,
    starts_with: string | undefined = undefined
  ) => {
    return axios.get(
      `${window.location.origin}/cache-api/api/storyblok/cache/`,
      {
        params: {
          version: "published",
          per_page: itemsPerPage,
          component: "Page",
          excludeFromGlobalSearch: false,
          query_filter: query,
          page: 1,
          isPopular: popular,
          categories: category,
          tag: tag,
          starts_with: starts_with,
        },
      }
    );
  };

  static getStoryblokSearch = (
    query: string | null,
    itemsPerPage: number | undefined,
    popular: boolean | null = null,
    category: string | null = null,
    tag: string | null = null,
    starts_with: string | undefined = undefined
  ) => {
    return getStoryblokApi().getStories({
      token: process.env.REACT_APP_STORYBLOK_ACCESS_TOKEN,
      version: "published",
      per_page: itemsPerPage,
      starts_with: starts_with || "",
      filter_query: {
        component: { in: "Page" },
        "body.isPopular": { in: popular },
        "body.categories": { in: category },
        "body.tags": { in: tag },
        excludeFromGlobalSearch: { in: false },
        __or: [
          {
            shortTitle: {
              like: `*${query}*`,
            },
          },
          {
            standfirst: {
              like: `*${query}*`,
            },
          },
        ],
      },
    });
  };

  static createTicket = async (
    values: ContactUSFormFields,
    files: Array<File>
  ) => {
    const uploadTokens = await SupportUtils.uploadFile(files);

    const zendeskEndpoint = this.zendeskUrl + "requests";
    const zendeskAuth = process.env.REACT_APP_ZENDESK_ACCESS_TOKEN;

    const data: ZendeskBody = {
      request: {
        subject: values.subject,
        tags: ["web_widget"],
        ticket_form_id: 1260809770190,
        via_id: 48,
        comment: {
          body: values.message,
          uploads: uploadTokens,
        },
        fields: {
          "1260823830209": values.davAudioHost,
          "1900003373844": values.help?.value,
          "1900003374764": values.operatingSystemVersion,
        },
        requester: {
          email: values.email,
          local_id: 1,
          name: "Anonymous",
        },
      },
    };

    return axios.post(zendeskEndpoint, data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: zendeskAuth,
      },
    });
  };

  static uploadFile = async (files: Array<File>): Promise<string[]> => {
    const uploadTokens: string[] = [];
    const zendeskEndpoint = this.zendeskUrl + "uploads";
    const zendeskAuth = process.env.REACT_APP_ZENDESK_ACCESS_TOKEN;

    for (const file of files) {
      const formData = new FormData();
      formData.append("uploaded_data", file);

      const response = await axios.post(
        zendeskEndpoint +
          "?filename=" +
          encodeURIComponent(file.name) +
          "&via_id=48",
        formData,
        {
          headers: {
            Authorization: zendeskAuth,
          },
        }
      );

      const token = response.data.upload.token;
      uploadTokens.push(token);
    }

    return uploadTokens;
  };

  static helpOptions = [
    {
      label: "Audiomovers for Education",
      value: "about_audiomovers-education",
    },
    { label: "Audiomovers for Business", value: "about_b2b" },
    { label: "Listento Standalone App", value: "about_listento-app" },
    { label: "Listento Plugin", value: "about_listento-plugin" },
    { label: "Omnibus", value: "omnibus" },
    { label: "Minibus", value: "minibus" },
    { label: "Binaural Renderer", value: "binaural_renderer" },
    { label: "Inject", value: "inject" },
    { label: "Listento Mobile", value: "about_listento-mobile" },
    { label: "Beta feedback", value: "beta_feedback" },
    { label: "Billing / License Admin", value: "about_billing" },
    { label: "Login Issues", value: "about_login" },
    { label: "Marketing & Partnerships", value: "about_marketing-partnership" },
    { label: "Feature Requests", value: "feature_requests" },
    { label: "(other)", value: "about_other" },
  ];
}

export default SupportUtils;
